import { useEffect, useRef, useState } from 'react'
import { SearchRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FilledInput,
  InputAdornment,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import {
  useGetOneStopHyundaiMenusQuery,
  useSaveOneStopHyundaiMutation
} from 'store/one-stop-hyundai'
import Icon from 'components/icon'
import Notification from 'components/notification'
import type { OneStopHyundaiItemProps } from 'modules/types'

const OneStopHyundaiPage = () => {
  const { data, isLoading } = useGetOneStopHyundaiMenusQuery()
  const [saveOneStopHyundai, saveResult] = useSaveOneStopHyundaiMutation()

  const [draftItems, setDraftItems] = useState<OneStopHyundaiItemProps[]>([])
  const [isDirty, setIsDirty] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [notification, setNotification] = useState({ open: false, message: '', isError: false })

  const dragIndexRef = useRef<number | null>(null)

  useEffect(() => {
    if (data) {
      const sorted = [...data].sort((a, b) => {
        if (a.sort === null) return 1
        if (b.sort === null) return -1
        return a.sort - b.sort
      })
      setDraftItems(sorted)
    }
  }, [data])

  useEffect(() => {
    if (saveResult.isSuccess) {
      setIsDirty(false)
      setConfirmOpen(false)
      setNotification({ open: true, message: 'Arrangement saved successfully', isError: false })
    }
    if (saveResult.isError) {
      setNotification({ open: true, message: 'Failed to save arrangement', isError: true })
    }
  }, [saveResult.isSuccess, saveResult.isError])

  const onVisibilityToggle = (id: number) => {
    setDraftItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isActive: !item.isActive } : item))
    )
    setIsDirty(true)
  }

  const onDragStart = (index: number) => {
    dragIndexRef.current = index
  }

  const onDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    const from = dragIndexRef.current
    if (from === null || from === index) return

    setDraftItems((prev) => {
      const updated = [...prev]
      const [moved] = updated.splice(from, 1)
      updated.splice(index, 0, moved)
      dragIndexRef.current = index
      return updated
    })
    setIsDirty(true)
  }

  const onDragEnd = () => {
    dragIndexRef.current = null
  }

  const onSaveConfirm = () => {
    const payload = draftItems.map((item, i) => ({
      id: item.id,
      sort: i + 1,
      isActive: item.isActive ?? false,
      menuCode: item.menuCode
    }))
    saveOneStopHyundai(payload)
  }

  const filteredItems = draftItems.filter((item) =>
    item.name.toLowerCase().includes(search.toLowerCase())
  )

  const displayItems = search ? filteredItems : draftItems

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ display: 'flex', padding: 2, gap: 2, flexDirection: 'column' }}
    >
      <Box display='flex' width='100%' justifyContent='space-between' alignItems='center'>
        <Typography variant='h6' fontWeight={500}>
          One Stop Hyundai
        </Typography>
        <Box display='flex' gap={2} alignItems='center' flexWrap='wrap'>
          <FilledInput
            size='small'
            placeholder='Search'
            value={search}
            hiddenLabel
            onChange={(e) => setSearch(e.target.value)}
            startAdornment={
              <InputAdornment position='start'>
                <SearchRounded />
              </InputAdornment>
            }
          />
          <LoadingButton
            variant='contained'
            disabled={!isDirty}
            loading={saveResult.isLoading}
            loadingIndicator={<CircularProgress color='inherit' size={20} />}
            onClick={() => setConfirmOpen(true)}
            startIcon={<Icon icon='Save' />}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Save Arrangement
          </LoadingButton>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: '70vh' }}>
          <Table aria-label='One Stop Hyundai Table' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '20px' }}>Action</TableCell>
                <TableCell sx={{ width: '50px', whiteSpace: 'nowrap' }} align='center'>
                  Visibility
                </TableCell>
                <TableCell sx={{ width: '20px' }}>No</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Menu Item Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayItems.map((item, index) => {
                const actualIndex = draftItems.findIndex((d) => d.id === item.id)
                return (
                  <TableRow
                    key={item.id}
                    draggable={!search}
                    onDragStart={() => onDragStart(actualIndex)}
                    onDragOver={(e) => onDragOver(e, actualIndex)}
                    onDragEnd={onDragEnd}
                    sx={{ cursor: search ? 'default' : 'grab', '&:active': { cursor: 'grabbing' } }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          color: search ? 'text.disabled' : 'text.primary',
                          fontSize: 20,
                          lineHeight: 1,
                          userSelect: 'none'
                        }}
                      >
                        ⠿
                      </Typography>
                    </TableCell>
                    <TableCell align='center'>
                      <Switch
                        checked={item.isActive ?? false}
                        onChange={() => onVisibilityToggle(item.id)}
                        color='primary'
                      />
                    </TableCell>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.name}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogContent>
          <DialogContentText>Are you sure to re-arrange One Stop Hyundai item?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <LoadingButton onClick={() => setConfirmOpen(false)} color='inherit'>
            Cancel
          </LoadingButton>
          <LoadingButton
            onClick={onSaveConfirm}
            variant='contained'
            loading={saveResult.isLoading}
            loadingIndicator={<CircularProgress size={20} color='inherit' />}
          >
            Yes
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Notification
        open={notification.open}
        isError={notification.isError}
        message={notification.message}
        onClose={() => setNotification((n) => ({ ...n, open: false }))}
      />
    </Container>
  )
}

export default OneStopHyundaiPage
